import React, { useState } from 'react';
import { FollowerGroup, Community } from '../types';
import { stickerTextStyle } from './StickerText';
import { CloseIcon, CheckmarkIcon, UsersIcon, HashtagIcon, PrivateIcon } from './Icons';
import { useAuth } from '../contexts/AuthContext';

interface LinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    friends: string[];
    followers: string[];
    followerGroups: FollowerGroup[];
    communities: Community[];
    onSubmit: (targetUsers: string[], text?: string, imageFile?: File, sendLocation?: boolean) => void;
    themeColor?: string;
}

export const LinkModal: React.FC<LinkModalProps> = ({
    isOpen,
    onClose,
    friends,
    followers,
    followerGroups,
    communities,
    onSubmit,
    themeColor = '#3b82f6'
}) => {
    const { currentUser, usersData } = useAuth();
    const currentUserData = usersData[currentUser];
    const userThemeColor = currentUserData?.appThemeColor || themeColor;
    const userTextColor = currentUserData?.appTextColor || '#ffffff';
    const userOpacity = (currentUserData?.postBlurOpacity !== undefined ? currentUserData.postBlurOpacity : 50) / 100;

    const [selectedFollowers, setSelectedFollowers] = useState<Set<string>>(new Set());
    const [selectedGroups, setSelectedGroups] = useState<Set<string>>(new Set());
    const [selectedCommunities, setSelectedCommunities] = useState<Set<string>>(new Set());

    const [isGroupsExpanded, setIsGroupsExpanded] = useState(true);
    const [isCommunitiesExpanded, setIsCommunitiesExpanded] = useState(true);
    const [isFollowersExpanded, setIsFollowersExpanded] = useState(true);

    const [text, setText] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    const [sendLocation, setSendLocation] = useState(false);

    const combinedConnections = Array.from(new Set([...(friends || []), ...(followers || [])]));
    const followerLabel = currentUserData?.followerNickname || 'Followers';

    const isSubmitDisabled = selectedFollowers.size === 0 && selectedGroups.size === 0 && selectedCommunities.size === 0;

    const getExactModalBg = (color: string, opacityVal: number) => {
        if (!color) return `rgba(26, 26, 46, ${opacityVal})`;
        if (color.startsWith('rgba')) return color.replace(/[\d.]+\)$/, `${opacityVal})`);
        if (color.startsWith('rgb')) return color.replace('rgb', 'rgba').replace(')', `, ${opacityVal})`);
        let hex = color.replace('#', '');
        if (hex.length === 3 || hex.length === 4) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        return (!isNaN(r) && !isNaN(g) && !isNaN(b)) ? `rgba(${r}, ${g}, ${b}, ${opacityVal})` : `rgba(26, 26, 46, ${opacityVal})`;
    };

    const modalBg = getExactModalBg(userThemeColor, userOpacity);

    const handleSubmit = () => {
        const targetUsers = new Set<string>();

        // Add individual followers
        selectedFollowers.forEach(f => targetUsers.add(f));

        // Add group members
        selectedGroups.forEach(groupId => {
            const group = followerGroups.find(g => g.id === groupId);
            if (group) {
                group.followers.forEach(f => targetUsers.add(f));
            }
        });

        // Add community members
        selectedCommunities.forEach(communityId => {
            const comm = communities.find(c => c._id === communityId);
            if (comm && comm.members) {
                comm.members.forEach(m => targetUsers.add(m));
            }
        });

        onSubmit(Array.from(targetUsers), text, imageFile || undefined, sendLocation);
        setSelectedFollowers(new Set());
        setSelectedGroups(new Set());
        setSelectedCommunities(new Set());
        setText('');
        setImageFile(null);
        setImagePreview(null);
        setSendLocation(false);
    };

    if (!isOpen) return null;

    const toggleSelection = (id: string, set: Set<string>, setter: React.Dispatch<React.SetStateAction<Set<string>>>) => {
        const newSet = new Set(set);
        if (newSet.has(id)) {
            newSet.delete(id);
        } else {
            newSet.add(id);
        }
        setter(newSet);
    };

    const renderCheckbox = (isSelected: boolean) => (
        <div
            className="w-5 h-5 rounded border flex items-center justify-center transition-all duration-300"
            style={{
                borderColor: isSelected ? userThemeColor : 'rgba(255, 255, 255, 0.2)',
                backgroundColor: isSelected ? userThemeColor : 'rgba(0, 0, 0, 0.2)'
            }}
        >
            {isSelected && <CheckmarkIcon className="w-3 h-3 text-white" />}
        </div>
    );

    const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
        <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
    );

    return (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-lg animate-fade-in">
            <div
                className="rounded-[2.5rem] w-full max-w-md overflow-hidden flex flex-col max-h-[85vh] border border-white/10 shadow-2xl backdrop-blur-md animate-scale-in"
                style={{ backgroundColor: modalBg, color: userTextColor }}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-6 shrink-0 bg-transparent">
                    <div>
                        <h2 className="text-xl font-bold" style={{ color: userThemeColor, ...stickerTextStyle(0.15) }}>Who you tryna link with?</h2>
                        <p className="text-xs opacity-75 mt-1" style={stickerTextStyle(0.18)}>Ask your friends to hang out! Send them a pin!</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-full hover:bg-white/10 transition-colors shrink-0"
                    >
                        <CloseIcon className="w-5 h-5" style={{ color: userTextColor }} />
                    </button>
                </div>

                {/* Body - Scrollable */}
                <div
                    className="p-6 overflow-y-auto space-y-6 flex-1 no-scrollbar"
                    onTouchStart={(e) => e.stopPropagation()}
                    onTouchMove={(e) => e.stopPropagation()}
                >
                    {/* Text and Image Input */}
                    <div className="bg-black/20 p-4 rounded-2xl">
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder="What's the move?"
                            className="w-full bg-black/30 rounded-2xl p-3 outline-none resize-none placeholder-white/30 transition-colors text-left"
                            rows={2}
                            style={{ color: userTextColor, ...stickerTextStyle(0.18) }}
                        />
                        <div className="flex items-center gap-4 mt-3">
                            <label className="cursor-pointer opacity-70 hover:opacity-100 transition-opacity flex items-center gap-2">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                            setImageFile(file);
                                            const url = URL.createObjectURL(file);
                                            setImagePreview(url);
                                        }
                                    }}
                                />
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-sm font-medium" style={stickerTextStyle(0.18)}>Attach Image</span>
                            </label>
                            {imagePreview && (
                                <div className="relative w-12 h-12">
                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover rounded-lg border border-white/10" />
                                    <button
                                        onClick={(e) => { e.preventDefault(); setImageFile(null); setImagePreview(null); }}
                                        className="absolute -top-2 -right-2 bg-black/60 rounded-full p-1 border border-white/10 text-white/70 hover:text-white"
                                    >
                                        <CloseIcon className="w-3 h-3" />
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Location Toggle */}
                    <div className="flex items-center justify-between p-3.5 bg-black/20 rounded-2xl">
                        <div className="flex flex-col text-left">
                            <span className="text-sm font-semibold" style={stickerTextStyle(0.15)}>Send Location</span>
                            <span className="text-[10px] opacity-60" style={stickerTextStyle(0.18)}>Drop them a pin!</span>
                        </div>
                        <button
                            type="button"
                            onClick={() => setSendLocation(!sendLocation)}
                            className={`w-11 h-6 flex items-center rounded-full p-1 cursor-pointer transition-colors duration-300 ${sendLocation ? 'bg-green-500' : 'bg-white/10'}`}
                        >
                            <div
                                className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${sendLocation ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                        </button>
                    </div>

                    {/* Follower Groups */}
                    {followerGroups.length > 0 && (
                        <div>
                            <button
                                onClick={() => setIsGroupsExpanded(!isGroupsExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    <UsersIcon className="w-4 h-4" /> Groups
                                </h3>
                                <ChevronIcon expanded={isGroupsExpanded} />
                            </button>
                            {isGroupsExpanded && (
                                <div className="space-y-2 animate-fade-in">
                                    {followerGroups.map(group => (
                                        <div
                                            key={group.id}
                                            onClick={() => toggleSelection(group.id, selectedGroups, setSelectedGroups)}
                                            className="flex items-center justify-between p-3 rounded-2xl bg-black/20 hover:bg-black/40 border border-white/5 cursor-pointer transition-colors"
                                        >
                                            <div className="text-left">
                                                <div className="font-semibold" style={stickerTextStyle(0.15)}>{group.name}</div>
                                                <div className="text-xs opacity-60" style={stickerTextStyle(0.18)}>{group.followers.length} members</div>
                                            </div>
                                            {renderCheckbox(selectedGroups.has(group.id))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Communities */}
                    {communities.length > 0 && (
                        <div>
                            <button
                                onClick={() => setIsCommunitiesExpanded(!isCommunitiesExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2 opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    <PrivateIcon className="w-4 h-4" /> Communities
                                </h3>
                                <ChevronIcon expanded={isCommunitiesExpanded} />
                            </button>
                            {isCommunitiesExpanded && (
                                <div className="space-y-2 animate-fade-in">
                                    {communities.map(comm => (
                                        <div
                                            key={comm._id}
                                            onClick={() => toggleSelection(comm._id, selectedCommunities, setSelectedCommunities)}
                                            className="flex items-center justify-between p-3 rounded-2xl bg-black/20 hover:bg-black/40 border border-white/5 cursor-pointer transition-colors"
                                        >
                                            <div className="flex items-center gap-3 text-left">
                                                {comm.avatar ? (
                                                    <img src={comm.avatar} alt="" className="w-8 h-8 rounded-lg object-cover" />
                                                ) : (
                                                    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center border border-white/10">
                                                        <HashtagIcon className="w-4 h-4 opacity-50" />
                                                    </div>
                                                )}
                                                <div>
                                                    <div className="font-semibold" style={stickerTextStyle(0.15)}>{comm.name}</div>
                                                    <div className="text-xs opacity-60" style={stickerTextStyle(0.18)}>{comm.members.length} members</div>
                                                </div>
                                            </div>
                                            {renderCheckbox(selectedCommunities.has(comm._id))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Individual Followers & Friends */}
                    {combinedConnections.length > 0 && (
                        <div>
                            <button
                                onClick={() => setIsFollowersExpanded(!isFollowersExpanded)}
                                className="w-full flex items-center justify-between mb-3 group"
                            >
                                <h3 className="text-sm font-semibold uppercase tracking-wider opacity-80 group-hover:opacity-100 transition-opacity" style={stickerTextStyle(0.15)}>
                                    {followerLabel}
                                </h3>
                                <ChevronIcon expanded={isFollowersExpanded} />
                            </button>
                            {isFollowersExpanded && (
                                <div className="space-y-2 animate-fade-in">
                                    {combinedConnections.map(username => (
                                        <div
                                            key={username}
                                            onClick={() => toggleSelection(username, selectedFollowers, setSelectedFollowers)}
                                            className="flex items-center justify-between p-3 rounded-2xl bg-black/20 hover:bg-black/40 border border-white/5 cursor-pointer transition-colors"
                                        >
                                            <div className="text-left">
                                                <div className="font-semibold" style={stickerTextStyle(0.15)}>
                                                    {usersData[username]?.displayName || username}
                                                </div>
                                                {usersData[username]?.displayName && (
                                                    <div className="text-xs opacity-60" style={stickerTextStyle(0.18)}>
                                                        @{username}
                                                    </div>
                                                )}
                                            </div>
                                            {renderCheckbox(selectedFollowers.has(username))}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {combinedConnections.length === 0 && followerGroups.length === 0 && communities.length === 0 && (
                        <div className="text-center py-8 opacity-50 text-sm" style={stickerTextStyle(0.18)}>
                            You don't have anyone to send a link request to yet.
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 shrink-0 bg-transparent">
                    <button
                        onClick={handleSubmit}
                        disabled={isSubmitDisabled}
                        style={{
                            backgroundColor: isSubmitDisabled ? 'rgba(255, 255, 255, 0.05)' : userThemeColor,
                            color: isSubmitDisabled ? 'rgba(255, 255, 255, 0.2)' : '#ffffff',
                            ...(!isSubmitDisabled ? stickerTextStyle(0.15) : {})
                        }}
                        className="w-full py-3.5 rounded-[1.5rem] font-bold text-lg transition-all duration-300"
                    >
                        Send Link Request
                    </button>
                </div>
            </div>
        </div>
    );
};
